import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { cn } from '../lib/utils';

const StatCard = ({ title, value, icon: Icon, color = 'blue', className }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value) || 0;
    if (end === 0) {
      setDisplayValue(0);
      return;
    }
    
    const duration = 1000; // 1 second
    const increment = end / (duration / 16); // 60fps
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  const colorClasses = {
    orange: 'text-orange-500 bg-orange-500/10 border-orange-500/20 glow-orange',
    blue: 'text-blue-500 bg-blue-500/10 border-blue-500/20 glow-blue',
    green: 'text-green-500 bg-green-500/10 border-green-500/20 glow-green',
    yellow: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20 glow-yellow',
    gold: 'text-yellow-600 bg-yellow-600/10 border-yellow-600/20 glow-yellow',
    red: 'text-red-500 bg-red-500/10 border-red-500/20 glow-red',
    purple: 'text-purple-500 bg-purple-500/10 border-purple-500/20 glow-purple',
    cyan: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20 glow-cyan',
  };

  return (
    <Card className={cn(
      "stat-card group relative overflow-hidden p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-1",
      "bg-card shadow-sm hover:shadow-xl dark:bg-[#12141c]/80 dark:backdrop-blur-xl dark:hover:bg-[#1a1d29]/90",
      "border-slate-100 dark:border-white/10 dark:hover:border-primary/30",
      className
    )}>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {title}
          </p>
          <h3 className="text-3xl font-bold tracking-tight text-foreground transition-all duration-300 group-hover:scale-105 origin-left">
            {displayValue.toLocaleString()}
          </h3>
        </div>
        
        <div className={cn(
          "flex items-center justify-center w-12 h-12 rounded-xl border transition-all duration-500 group-hover:rotate-12 group-hover:scale-110",
          colorClasses[color] || colorClasses.blue
        )}>
          {Icon && <Icon className="w-6 h-6" />}
        </div>
      </div>
      
      {/* Bottom Accent Line */}
      <div className={cn(
        "absolute bottom-0 left-0 h-[3px] w-0 transition-all duration-500 group-hover:w-full",
        color === 'orange' && "bg-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.8)]",
        color === 'blue' && "bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.8)]",
        color === 'green' && "bg-green-500 shadow-[0_0_15px_rgba(34,197,94,0.8)]",
        color === 'yellow' && "bg-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.8)]",
        color === 'gold' && "bg-yellow-600 shadow-[0_0_15px_rgba(202,138,4,0.8)]",
        color === 'red' && "bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.8)]",
        color === 'purple' && "bg-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.8)]",
        color === 'cyan' && "bg-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.8)]"
      )} />

      {/* Background Decor */}
      <div className={cn(
        "absolute -bottom-4 -right-4 w-24 h-24 rounded-full blur-3xl opacity-10",
        color === 'orange' && "bg-orange-500",
        color === 'blue' && "bg-blue-500",
        color === 'green' && "bg-green-500",
        color === 'yellow' && "bg-yellow-500",
        color === 'gold' && "bg-yellow-600",
        color === 'red' && "bg-red-500",
        color === 'purple' && "bg-purple-500",
        color === 'cyan' && "bg-cyan-500"
      )} />
    </Card>
  );
};

export default StatCard;
